/*
  Cart page behavior for sections/sw-cart.liquid.

  Remove, quantity, and leather "Add" use the Cart AJAX API and re-render this
  section through the Section Rendering API. An empty cart reloads so the layout
  can switch back to the site header and footer.
*/
(() => {
  if (customElements.get('sw-cart')) return;

  const routes = window.routes || {};
  const CART_URL = routes.cart_url || '/cart';
  const CHANGE_URL = routes.cart_change_url || '/cart/change';
  const ADD_URL = routes.cart_add_url || '/cart/add';
  const BUBBLE_SECTION = 'cart-icon-bubble';

  const jsonUrl = (url) => (url.endsWith('.js') ? url : `${url}.js`);

  const request = (url, body) =>
    fetch(jsonUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(
          data.description || data.message || (window.cartStrings && window.cartStrings.error) || 'Something went wrong.'
        );
        error.data = data;
        throw error;
      }
      return data;
    });

  class SwCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const toggle = event.target.closest('[data-sw-summary-toggle]');
      if (toggle) {
        event.preventDefault();
        this.toggleSummary(toggle);
        return;
      }

      const remove = event.target.closest('[data-sw-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.changeLine(Number(remove.dataset.line), 0, remove);
        return;
      }

      const qty = event.target.closest('[data-sw-cart-qty]');
      if (qty) {
        event.preventDefault();
        this.changeLine(Number(qty.dataset.line), Math.max(0, Number(qty.dataset.quantity)), qty);
        return;
      }

      const add = event.target.closest('[data-sw-cart-add]');
      if (add) {
        event.preventDefault();
        this.addVariant(add.dataset.variantId, add);
      }
    }

    sectionsBody(extra) {
      return {
        ...extra,
        sections: [this.sectionId, BUBBLE_SECTION],
        sections_url: CART_URL,
      };
    }

    changeLine(line, quantity, trigger) {
      if (!line || this.busy) return;
      const lineEl = this.querySelector(`[data-sw-cart-line="${line}"]`);
      if (quantity === 0 && lineEl) lineEl.classList.add('is-removing');
      this.setBusy(true, trigger);

      request(CHANGE_URL, this.sectionsBody({ line, quantity }))
        .then((cart) => this.render(cart.sections || {}, cart))
        .catch((error) => {
          lineEl?.classList.remove('is-removing');
          this.showError(error.message);
        })
        .finally(() => this.setBusy(false, trigger));
    }

    addVariant(variantId, trigger) {
      const id = Number(variantId);
      if (!id || this.busy) return;
      this.setBusy(true, trigger);

      request(ADD_URL, this.sectionsBody({ items: [{ id, quantity: 1 }] }))
        .then((result) => this.render(result.sections || {}, result))
        .catch((error) => this.showError(error.message))
        .finally(() => this.setBusy(false, trigger));
    }

    render(sections) {
      const html = sections[this.sectionId];
      if (!html) {
        window.location.reload();
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('sw-cart');
      if (!next || next.dataset.itemCount === '0') {
        window.location.reload();
        return;
      }

      const nextRoot = next.querySelector('[data-sw-cart-root]');
      const root = this.querySelector('[data-sw-cart-root]');
      if (nextRoot && root) root.replaceWith(nextRoot);
      this.dataset.itemCount = next.dataset.itemCount;
      this.classList.remove('sw-cart--empty');
      this.showError('');
      this.updateBubble(sections);
    }

    updateBubble(sections) {
      const bubble = document.getElementById(BUBBLE_SECTION);
      const html = sections[BUBBLE_SECTION];
      if (!bubble || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.shopify-section') || doc.body;
      bubble.innerHTML = inner.innerHTML;
    }

    toggleSummary(button) {
      const panel = this.querySelector('[data-sw-summary-panel]');
      const label = button.querySelector('[data-sw-summary-label]');
      if (!panel) return;

      const open = button.getAttribute('aria-expanded') === 'true';
      if (open) {
        panel.style.height = `${panel.scrollHeight}px`;
        requestAnimationFrame(() => {
          panel.style.height = '0px';
        });
        button.setAttribute('aria-expanded', 'false');
        if (label) label.textContent = 'Show order summary';
        return;
      }

      panel.style.height = `${panel.scrollHeight}px`;
      button.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = 'Hide order summary';
      panel.addEventListener(
        'transitionend',
        () => {
          if (button.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
        },
        { once: true }
      );
    }

    setBusy(busy, trigger) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (trigger && trigger.isConnected) {
        trigger.classList.toggle('is-loading', busy);
        if ('disabled' in trigger) trigger.disabled = busy;
      }
    }

    showError(message) {
      const box = this.querySelector('[data-sw-cart-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }
  }

  customElements.define('sw-cart', SwCart);
})();
